import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Twinbound (Act III, ACT3-BIBLE.md §8) — a Manifold echo.
 *
 * They spawn in mirrored pairs and each heals the other, so killing one and
 * walking away accomplishes nothing: the survivor rebuilds its partner's twin
 * from nothing. Both have to die inside `graceSeconds` of each other. It is the
 * Manifold's dilemma inverted and made cheap — the boss makes you protect two
 * things at once; the Twinbound makes you *kill* two things at once — and that
 * inversion is deliberate, because a wave-stage creature that split your body
 * would be teaching the boss's answer rather than its question.
 *
 * `partner` is assigned after the wave has spawned (MinionWaveSystem._pairTwins),
 * the same way a Warden's link is; an unpaired Twinbound is simply a plain
 * chaser, which is the right degradation.
 */
export class Twinbound {
  constructor(eventBus, { x, y, radius = 4, hp, moveSpeed, contactDamage, healPerSecond = 14, graceSeconds = 3 }) {
    this.eventBus = eventBus;
    this.type = 'twinbound';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.healPerSecond = healPerSecond;
    this.graceSeconds = graceSeconds;
    this.partner = null;
    this.isDead = false;
    // Counts up once the partner is down: while it runs, this one is alone and
    // can be finished for good. Let it lapse and it mends whole again.
    this._aloneFor = 0;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  /** True while this one is mourning a dead partner — drives the link's visual urgency. */
  get isSurvivor() {
    return !!this.partner && this.partner.isDead && !this.isDead;
  }

  update(dt, player) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    if (this.isSurvivor) {
      this._aloneFor += dt;
      if (this._aloneFor >= this.graceSeconds) {
        // The window lapsed. The survivor doesn't bring its partner back — a
        // dead minion is removed from the field the same tick it dies, and
        // reviving one would mean reaching into the wave system from an entity.
        // It simply mends itself whole, which costs the player the damage they
        // spent and asks the same question again: both, together, or neither.
        this._aloneFor = 0;
        this.hp = this.maxHp;
        this.eventBus.emit('twinbound:rejoined', { x: this.x, y: this.y, minion: this });
      }
    } else if (this.partner && !this.partner.isDead && !this.isDead) {
      // Both alive: each mends the other, so chip damage across the pair never
      // adds up. You have to commit to one.
      this.partner.hp = Math.min(this.partner.maxHp, this.partner.hp + this.healPerSecond * dt);
    }

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;
    this._clampToArena();

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this._aloneFor = 0;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
